import { Reservation, ReservationNote, ReservationTag } from '@/api/types/reservation';
import { ReservationModel, ReservationNoteModel, ReservationStatus, ReservationTagModel, ReservationTagType } from '@/models/reservation';
import { transformCustomer } from './customer';
import { transformProperty } from './property';
import { transformPropertyUnit } from './property-unit';
import { transformCompany } from './company'

function transformReservationStatus(status: string): ReservationStatus {
  switch (status) {
    case 'canceled': {
      return ReservationStatus.CANCELED;
    }

    case 'confirmed': {
      return ReservationStatus.CONFIRMED;
    }

    case 'optional': {
      return ReservationStatus.OPTIONAL;
    }

    case 'processed': {
      return ReservationStatus.PROCESSED;
    }

    case 'requested': {
      return ReservationStatus.REQUESTED;
    }

    case 'started': {
      return ReservationStatus.STARTED;
    }

    case 'enquired':
    default: {
      return ReservationStatus.ENQUIRED;
    }
  }
}

function transformReservationNote(note: ReservationNote): ReservationNoteModel {
  return {
    id: note.id,
    text: note.text
  };
}

function transformReservationNotes(notes: ReservationNote[]): ReservationNoteModel[] {
  return notes.map(transformReservationNote);
}

function transformReservationTagType(type: string): ReservationTagType {
  switch (type) {
    case 'arrived': {
      return ReservationTagType.ARRIVED;
    }

    case 'not-arrived': {
      return ReservationTagType.NOT_ARRIVED;
    }

    case 'paid': {
      return ReservationTagType.PAID;
    }

    case 'unpaid': {
      return ReservationTagType.UNPAID;
    }

    case 'overpaid': {
      return ReservationTagType.OVERPAID;
    }

    default: {
      return ReservationTagType.UNKNOWN;
    }
  }
}

function transformReservationTag(tag: ReservationTag): ReservationTagModel {
  const type = transformReservationTagType(tag.key);

  return {
    type,
    id: tag.id,
    label: tag.name
  };
}

function transformReservationTags(tags: ReservationTag[]): ReservationTagModel[] {
  return tags.map(transformReservationTag);
}

function transformReservation(reservation: Reservation): ReservationModel {
  const customer = reservation.customer && transformCustomer(reservation.customer);
  const property = reservation.property && transformProperty(reservation.property);
  const reservationNotes = reservation.reservationNotes && transformReservationNotes(reservation.reservationNotes);
  const status = transformReservationStatus(reservation.status);
  const checkInDate = reservation.checkInDate === null ? null : new Date(reservation.checkInDate);
  const reservationTags = reservation.reservationTags && transformReservationTags(reservation.reservationTags);
  const propertyUnit = reservation.propertyUnit && transformPropertyUnit(reservation.propertyUnit);
  const company = reservation.company && transformCompany(reservation.company);

  return {
    customer,
    property,
    checkInDate,
    status,
    reservationNotes,
    reservationTags,
    source: reservation.source,
    id: reservation.id,
    startDate: new Date(reservation.startDate),
    endDate: new Date(reservation.endDate),
    confirmationNumber: reservation.confirmationNumber,
    channelConfirmationNumber: reservation.channelConfirmationNumber,
    propertyUnit,
    company,
    slug: reservation.slug,
  };
}

export function transformReservations(reservations: Reservation[]): ReservationModel[] {
  return reservations.map(transformReservation);
}
